import React, { useState, useEffect, useCallback } from 'react';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/theme-dracula';
import 'ace-builds/src-noconflict/ext-language_tools';
import AxiosInstance from "axiosConfig";
import { FaEdit, FaTrash, FaExpand, FaCompress } from 'react-icons/fa';

const CodeEditor = () => {
  const [activeTab, setActiveTab] = useState('editor');
  const [language, setLanguage] = useState('python');
  const [code, setCode] = useState(
    `def SampleFunction(**kwargs):
  # necessary imports can be imported here
  # variables should always be extracted from **kwargs
  name = kwargs.get('name_of_the_person')
  location = kwargs.get('name_of_the_place')
  # after you have the necessary variables you logic goes below here
  message = 'Hello ' + str(name) + ' from ' + str(location)
  # you must return the final results
  return message

# Now, get results using function above, parse values for you keyword arguments
# keywords must match what has been made use of in kwargs.get in the function above
result = SampleFunction(name_of_the_person='Samuel', name_of_the_place='Diani')
`
  );
  const [scriptName, setScriptName] = useState('');
  const [scriptPurpose, setScriptPurpose] = useState('');
  const [error, setError] = useState('');
  const [simulationStatus, setSimulationStatus] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [BotLink_Dictionary, setBotlinkDict] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [scriptToDelete, setScriptToDelete] = useState(null);

  // Fetch BotLink dictionary from backend on mount
  useEffect(() => {
    const fetchBotLinkDictionary = async () => {
      try {
        const response = await AxiosInstance.get('/bot-link-config');
        console.log('BotLink API Response:', response.data);
        if (response.data.status === 'success' && response.data.message && typeof response.data.message === 'object' && !Array.isArray(response.data.message)) {
          setBotlinkDict(response.data.message);
          console.log('BotLink Dictionary Updated:', response.data.message);
        } else {
          setError('Invalid BotLink dictionary format from API.');
          setBotlinkDict({});
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching BotLink dictionary.');
        setBotlinkDict({});
      }
    };
    fetchBotLinkDictionary();
  }, []);

  // Log BotLink_Dictionary whenever it changes
  useEffect(() => {
    console.log('Current BotLink_Dictionary State:', BotLink_Dictionary);
  }, [BotLink_Dictionary]);

  // Custom autocompleter for BotLink
  const createBotLinkCompleter = useCallback(() => {
    return {
      identifierRegexps: [/[a-zA-Z_][a-zA-Z0-9_.-]*/],
      getCompletions: (editor, session, pos, prefix, callback) => {
        const line = session.getLine(pos.row).substring(0, pos.column);
        console.log('getCompletions called', { line, prefix, pos });

        // Match patterns like 'BotLink.', 'BotLink.Actions.', 'BotLink.Actions.OTPSend(', etc.
        const contextMatch = line.match(/(BotLink(?:\.[A-Za-z_][A-Za-z0-9_]*)*)(\(|\.)?$/);
        const contextPath = contextMatch ? contextMatch[1].split('.').filter(p => p) : [];
        const isAfterDot = contextMatch && contextMatch[2] === '.';
        const isAfterOpenParen = contextMatch && contextMatch[2] === '(';
        console.log('Context:', { contextPath, isAfterDot, isAfterOpenParen });

        let suggestions = [];
        let currentLevel = BotLink_Dictionary;

        // Navigate to the current level in the dictionary
        for (let i = 0; i < contextPath.length; i++) {
          if (currentLevel && typeof currentLevel === 'object' && !Array.isArray(currentLevel)) {
            currentLevel = currentLevel[contextPath[i]];
          } else {
            currentLevel = null;
            break;
          }
        }

        // Case-insensitive BotLink suggestion
        if (prefix.match(/^[Bb]/) && contextPath.length === 0 && !isAfterDot && !isAfterOpenParen) {
          suggestions.push({
            caption: 'BotLink',
            value: 'BotLink',
            meta: 'keyword',
            score: 1000,
          });
        }

        // Suggestions based on context
        if (currentLevel && typeof currentLevel === 'object' && !Array.isArray(currentLevel)) {
          if (contextPath.length === 1 && contextPath[0] === 'BotLink' && isAfterDot) {
            // Suggest 'Actions' and 'Data' after 'BotLink.'
            suggestions = Object.keys(currentLevel)
              .filter(key => key.toLowerCase().startsWith(prefix.toLowerCase()))
              .map(key => ({
                caption: key,
                value: key,
                meta: 'property',
                score: 900,
              }));
          } else if (contextPath.length === 2 && contextPath[0] === 'BotLink' && contextPath[1] === 'Actions' && isAfterDot) {
            // Suggest action functions after 'BotLink.Actions.'
            suggestions = Object.keys(currentLevel)
              .filter(key => key.toLowerCase().startsWith(prefix.toLowerCase()))
              .map(key => {
                const params = Array.isArray(currentLevel[key]) ? currentLevel[key] : [];
                const paramString = params.length > 0 ? `(${params.join(', ')})` : '()';
                return {
                  caption: `${key}${paramString}`,
                  value: `${key}${paramString}`,
                  meta: 'function',
                  score: 800,
                };
              });
          } else if (contextPath.length === 2 && contextPath[0] === 'BotLink' && contextPath[1] === 'Data' && isAfterDot) {
            // Suggest data keys after 'BotLink.Data.'
            suggestions = Object.keys(currentLevel)
              .filter(key => key.toLowerCase().startsWith(prefix.toLowerCase()))
              .map(key => ({
                caption: key,
                value: key,
                meta: 'variable',
                score: 800,
              }));
          } else if (contextPath.length >= 3 && contextPath[0] === 'BotLink' && contextPath[1] === 'Actions' && isAfterOpenParen && prefix === '') {
            // Suggest parameters after 'BotLink.Actions.<Action>('
            if (Array.isArray(currentLevel)) {
              const params = currentLevel.join(', ');
              suggestions = [{
                caption: params,
                value: params,
                meta: 'parameters',
                score: 800,
              }];
            }
          }
        }

        console.log('Suggestions:', suggestions);
        callback(null, suggestions);
      },
    };
  }, [BotLink_Dictionary]);

  // Register autocompleter
  useEffect(() => {
    const langTools = window.ace.acequire('ace/ext/language_tools');
    langTools.setCompleters([createBotLinkCompleter()]);
    console.log('BotLink completer registered');
    return () => {
      langTools.setCompleters([]);
    };
  }, [BotLink_Dictionary, createBotLinkCompleter]);

  const fetchScripts = useCallback(async () => {
    try {
      const response = await AxiosInstance.get('/get-custom-scripts');
      if (response.data.status === 'success') {
        const fetchedScripts = response.data.message.map(script => ({
          id: script.id.toString(),
          name: script.name,
          purpose: script.purpose,
          language: script.language === 'py' ? 'python' : 'javascript',
          code: script.code,
          status: script.status ? 'Active' : 'Inactive',
          processes: script.processes,
          result: script.result,
          expanded: false
        }));
        setScripts(fetchedScripts);
      } else {
        setError(response.data.message || 'Failed to fetch scripts.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching scripts from backend.');
    }
  }, []);

  // Fetch scripts from backend on mount
  useEffect(() => {
    fetchScripts();
  }, [fetchScripts]);

  const validateCode = useCallback((code, lang) => {
    try {
      if (lang === 'javascript') {
        try {
          // eslint-disable-next-line no-eval
          eval(`(function() { ${code} })()`);
        } catch (e) {
          throw new Error('Invalid JavaScript code: ' + e.message);
        }
        if (code.match(/\bvar\b|\blet\b|\bconst\b/) === null && code.match(/\b[a-zA-Z_]\w*\b/)) {
          throw new Error('Possible undeclared variable detected');
        }
      } else {
        const lines = code.split('\n');
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          if (line === '') continue;
          const spaces = lines[i].match(/^\s*/)[0].length;
          if (spaces % 2 !== 0 && line !== '') {
            throw new Error(`Indentation error on line ${i + 1}: Use 2 spaces for indentation`);
          }
          if (line.startsWith('def ') && !line.endsWith(':')) {
            throw new Error(`Syntax error on line ${i + 1}: Function definition missing colon`);
          }
          if (line.includes('elif') && !lines.slice(0, i).some(l => l.trim().startsWith('if '))) {
            throw new Error(`Syntax error on line ${i + 1}: 'elif' without preceding 'if'`);
          }
        }
        const significantLines = lines
          .map(line => line.trim())
          .filter(line => line && !line.startsWith('#'));
        if (significantLines.length === 0) {
          throw new Error("Python code must define a function and end with 'result =<function_call>'");
        }
        const functionDef = significantLines.find(line => line.match(/^def\s+\w+\s*\(\s*\*\*kwargs\s*\)\s*:/));
        if (!functionDef) {
          throw new Error("Python code must define a function with '**kwargs' as the only parameter");
        }
        const functionName = functionDef.match(/^def\s+(\w+)\s*\(\s*\*\*kwargs\s*\)\s*:/)[1];
        const kwargsGets = [];
        const kwargsVars = [];
        let inFunction = false;
        let functionIndent = 0;
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          if (line === '') continue;
          if (line.match(/^def\s+\w+\s*\(\s*\*\*kwargs\s*\)\s*:/)) {
            inFunction = true;
            functionIndent = lines[i].match(/^\s*/)[0].length;
            continue;
          }
          if (inFunction) {
            const currentIndent = lines[i].match(/^\s*/)[0].length;
            if (currentIndent <= functionIndent && line !== '') {
              inFunction = false;
              continue;
            }
            if (line.match(/^\s*import\s+\w+/)) {
              continue;
            }
            const assignmentMatch = line.match(/^\s*(\w+)\s*=\s*([^=].*)/);
            if (assignmentMatch) {
              const varName = assignmentMatch[1];
              const rhs = assignmentMatch[2].trim();
              const isKwargsGet = line.match(/^\s*\w+\s*=\s*kwargs\.get\s*\(\s*['"]([^'"]+)['"]\s*(?:,\s*[^)]+)?\s*\)/);
              if (isKwargsGet) {
                kwargsGets.push(isKwargsGet[1]);
                kwargsVars.push(varName);
                continue;
              }
              const isLiteral = rhs.match(/^(?:"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|-?\d+\.?\d*|[Tt]rue|[Ff]alse|[Nn]one)$/);
              if (isLiteral) {
                throw new Error(`Hard-coded literal assignment on line ${i + 1} is not allowed; use kwargs.get or expressions with variables`);
              }
              const hasKnownVar = kwargsVars.some(variable => rhs.includes(variable));
              const hasFunctionCall = rhs.match(/\w+\s*\(/);
              if (!hasKnownVar && !hasFunctionCall) {
                throw new Error(`Assignment on line ${i + 1} must use variables from kwargs.get or a function call`);
              }
              kwargsVars.push(varName);
            }
          }
        }
        if (kwargsGets.length === 0) {
          throw new Error("Function must extract at least one variable using kwargs.get");
        }
        let hasReturn = false;
        inFunction = false;
        functionIndent = 0;
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          if (line === '') continue;
          if (line.match(/^def\s+\w+\s*\(\s*\*\*kwargs\s*\)\s*:/)) {
            inFunction = true;
            functionIndent = lines[i].match(/^\s*/)[0].length;
            continue;
          }
          if (inFunction) {
            const currentIndent = lines[i].match(/^\s*/)[0].length;
            if (currentIndent <= functionIndent && line !== '') {
              inFunction = false;
              continue;
            }
            if (line.match(/^\s*return\s+\S.*/)) {
              hasReturn = true;
            }
          }
        }
        if (!hasReturn) {
          throw new Error("Function must include a return statement");
        }
        const lastLine = significantLines[significantLines.length - 1];
        if (!lastLine.match(/^\s*result\s*=\s*\w+\s*\(.+\)$/)) {
          throw new Error("Python code must end with 'result = <function_call>'");
        }
        const lastLineMatch = lastLine.match(/^\s*result\s*=\s*(\w+)\s*\((.*)\)$/);
        if (!lastLineMatch || lastLineMatch[1] !== functionName) {
          throw new Error("Last line must call the defined function with 'result = <function_name>(...)'");
        }
        const argsString = lastLineMatch[2];
        const args = argsString.split(',').map(arg => arg.trim().match(/^(\w+)\s*=/)?.[1]).filter(Boolean);
        if (args.length === 0) {
          throw new Error("Function call must include keyword arguments matching kwargs.get keys");
        }
        for (const arg of args) {
          if (!kwargsGets.includes(arg)) {
            throw new Error(`Keyword argument '${arg}' in function call does not match any kwargs.get key`);
          }
        }
        for (const key of kwargsGets) {
          if (!args.includes(key)) {
            throw new Error(`kwargs.get key '${key}' not provided in function call`);
          }
        }
        if (scriptName && scriptName !== functionName) {
          throw new Error("Function name must match script name");
        }
      }
      return '';
    } catch (e) {
      return e.message;
    }
  }, [scriptName]);

  useEffect(() => {
    if (language !== 'python' || !scriptName) return;
    const isValidName = /^[a-zA-Z_]\w*$/.test(scriptName);
    if (!isValidName) {
      setError('Script name must be a single word, starting with a letter or underscore, followed by letters, digits, or underscores');
      return;
    }
    const lines = code.split('\n');
    const functionDefIndex = lines.findIndex(line => line.match(/^def\s+\w+\s*\(\s*\*\*kwargs\s*\)\s*:/));
    const resultCallIndex = lines.findIndex(line => line.match(/^\s*result\s*=\s*\w+\s*\(.+\)$/));
    if (functionDefIndex === -1 || resultCallIndex === -1) {
      setCode(
        `def ${scriptName}(**kwargs):
  # necessary imports can be imported here
  # variables should always be extracted from **kwargs
  name = kwargs.get('name_of_the_person')
  location = kwargs.get('name_of_the_place')
  # after you have the necessary variables you logic goes below here
  message = 'Hello ' + str(name) + ' from ' + str(location)
  # you must return the final results
  return message

# Now, get results using function above, parse values for you keyword arguments
# keywords must match what has been made use of in kwargs.get in the function above
result = ${scriptName}(name_of_the_person='Samuel', name_of_the_place='Diani')
`
      );
    } else {
      const oldFunctionName = lines[functionDefIndex].match(/^def\s+(\w+)\s*\(\s*\*\*kwargs\s*\)\s*:/)[1];
      const newLines = lines.map((line, index) => {
        if (index === functionDefIndex) {
          return line.replace(`def ${oldFunctionName}(`, `def ${scriptName}(`);
        }
        if (index === resultCallIndex) {
          return line.replace(`result = ${oldFunctionName}(`, `result = ${scriptName}(`);
        }
        return line;
      });
      setCode(newLines.join('\n'));
    }
    setError(validateCode(code, language));
  }, [scriptName, language, code, validateCode]);

  const debouncedValidate = useCallback(() => {
    let timeout;
    return (code, lang) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setError(validateCode(code, lang));
      }, 500);
    };
  }, [validateCode])();

  const handleCodeChange = (value) => {
    setCode(value);
    debouncedValidate(value, language);
    setSimulationStatus(null);
  };

  const handleScriptNameChange = (value) => {
    setScriptName(value);
    const isValidName = /^[a-zA-Z_]\w*$/.test(value) || value === '';
    if (!isValidName && value) {
      setError('Script name must be a single word, starting with a letter or underscore, followed by letters, digits, or underscores');
    } else {
      setError(validateCode(code, language));
    }
  };

  const formatCode = () => {
    let formatted = code;
    if (language === 'python') {
      const lines = code.split('\n').map(line => {
        const trimmed = line.trim();
        if (trimmed === '') return '';
        const indent = line.match(/^\s*/)[0].length;
        const newIndent = Math.floor(indent / 2) * 2;
        return ' '.repeat(newIndent) + trimmed;
      });
      formatted = lines.join('\n');
    } else {
      formatted = code.replace(/^\s*|\s*$/gm, '').replace(/^/gm, '  ').trim();
    }
    setCode(formatted);
    setError(validateCode(formatted, language));
    setSimulationStatus(null);
  };

  const simulateScript = async () => {
    if (!scriptName || !scriptPurpose) {
      setError('Script name and purpose are required.');
      return;
    }
    if (error) {
      setError('Please fix code errors before simulating.');
      return;
    }
    try {
      const backendLanguage = language === 'python' ? 'py' : 'js';
      const wrappedCode = `\`\`\`\n${code}\n\`\`\``;
      const response = await AxiosInstance.post('/custom-script-simulate', {
        name: scriptName,
        purpose: scriptPurpose,
        language: backendLanguage,
        code: wrappedCode,
      });
      setSimulationStatus({
        success: response.data.validation_passed,
        message: response.data.message,
        result: response.data.result
      });
      setError('');
    } catch (err) {
      setSimulationStatus({
        success: false,
        message: err.response?.data?.message || 'Simulation error.'
      });
      setError('Failed to connect to simulation endpoint.');
    }
  };

  const saveScript = async () => {
    if (!simulationStatus || !simulationStatus.success) {
      setError('Please successfully simulate the script before saving.');
      return;
    }
    if (error) {
      setError('Please fix code errors before saving.');
      return;
    }
    if (!simulationStatus.result) {
      setError('Cannot save script: Simulation result is empty.');
      return;
    }
    try {
      const backendLanguage = language === 'python' ? 'py' : 'js';
      const wrappedCode = `\`\`\`\n${code}\n\`\`\``;
      const action_code = Date.now();
      const response = await AxiosInstance.post('/custom-script-save', {
        name: scriptName,
        purpose: scriptPurpose,
        language: backendLanguage,
        code: wrappedCode,
        fk_client_id: 1,
        action_code: action_code,
        convo_id_used: 1,
      });
      if (response.data.success) {
        const newScript = {
          id: action_code.toString(),
          name: scriptName,
          purpose: scriptPurpose,
          language: backendLanguage === 'py' ? 'python' : 'javascript',
          code: code,
          status: 'Active',
          processes: response.data.processes || [`Used in process ${Math.floor(Math.random() * 100)}`],
          result: simulationStatus.result,
          expanded: false
        };
        setScripts([...scripts, newScript]);
        setScriptName('');
        setScriptPurpose('');
        setCode(language === 'python'
          ? `def SampleFunction(**kwargs):
  # necessary imports can be imported here
  # variables should always be extracted from **kwargs
  name = kwargs.get('name_of_the_person')
  location = kwargs.get('name_of_the_place')
  # after you have the necessary variables you logic goes below here
  message = 'Hello ' + str(name) + ' from ' + str(location)
  # you must return the final results
  return message

# Now, get results using function above, parse values for you keyword arguments
# keywords must match what has been made use of in kwargs.get in the function above
result = SampleFunction(name_of_the_person='Samuel', name_of_the_place='Diani')
`
          : '// Write your JavaScript code here\n');
        setError('');
        setSimulationStatus(null);
        setActiveTab('scripts');
      } else {
        setError(response.data.message || 'Failed to save script.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving script.');
    }
  };

  const handleDeleteClick = (scriptId, scriptName) => {
    setScriptToDelete({ id: scriptId, name: scriptName });
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!scriptToDelete) return;
    try {
      const response = await AxiosInstance.delete(`/custom-script-delete?script_id=${scriptToDelete.id}`);
      if (response.data.success) {
        setScripts(scripts.filter(s => s.id !== scriptToDelete.id));
      } else {
        setError(response.data.message || `Error deleting script ${scriptToDelete.name}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || `Error deleting script ${scriptToDelete.name}`);
    } finally {
      setShowDeleteDialog(false);
      setScriptToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setScriptToDelete(null);
  };

  const toggleScriptExpand = (scriptId) => {
    setScripts(scripts.map(s =>
      s.id === scriptId ? { ...s, expanded: !s.expanded } : s
    ));
  };

  const handleEdit = (script) => {
    setScriptName(script.name);
    setScriptPurpose(script.purpose);
    setLanguage(script.language);
    setCode(script.code);
    setError('');
    setSimulationStatus(null);
    setActiveTab('editor');
  };

  const handleLanguageChange = (e) => {
    const newLang = e.target.value;
    setLanguage(newLang);
    setCode(newLang === 'python'
      ? `def SampleFunction(**kwargs):
  # necessary imports can be imported here
  # variables should always be extracted from **kwargs
  name = kwargs.get('name_of_the_person')
  location = kwargs.get('name_of_the_place')
  # after you have the necessary variables you logic goes below here
  message = 'Hello ' + str(name) + ' from ' + str(location)
  # you must return the final results
  return message

# Now, get results using function above, parse values for you keyword arguments
# keywords must match what has been made use of in kwargs.get in the function above
result = SampleFunction(name_of_the_person='Samuel', name_of_the_place='Diani')
`
      : '// Write your JavaScript code here\n');
    setError('');
    setSimulationStatus(null);
    setScriptName('');
  };

  const renderWithNewlines = (data) => {
    if (!data) return 'N/A';
    if (typeof data === 'string') {
      return data.split('\n').map((line, index) => (
        <React.Fragment key={index}>
          {line}
          {index < data.split('\n').length - 1 && <br />}
        </React.Fragment>
      ));
    }
    if (typeof data === 'object' && data !== null) {
      return (
        <React.Fragment>
          {Object.entries(data).map(([key, value], index) => (
            <div key={index}>
              <strong>{key}:</strong> {JSON.stringify(value)}
            </div>
          ))}
        </React.Fragment>
      );
    }
    return 'Unsupported data type';
  };

  const truncateResult = (text) => {
    if (!text) return 'N/A';
    const truncated = text.slice(0, 100);
    return truncated + (text.length > 100 ? '...' : '');
  };

  return (
    <div className="h-full bg-gray-900 text-white p-4 font-sans" role="region" aria-label="Code Editor">
      <div className="max-w-4xl mx-auto h-full flex flex-col">
        <nav className="flex border-b border-gray-700 mb-4" role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'editor'}
            className={`px-4 py-2 ${activeTab === 'editor' ? 'bg-gray-700' : 'bg-gray-800'} hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500`}
            onClick={() => setActiveTab('editor')}
          >
            Code Editor
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'scripts'}
            className={`px-4 py-2 ${activeTab === 'scripts' ? 'bg-gray-700' : 'bg-gray-800'} hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500`}
            onClick={() => setActiveTab('scripts')}
          >
            Scripts
          </button>
        </nav>
        {activeTab === 'editor' && (
          <div className="space-y-4 flex-1 flex flex-col" role="tabpanel">
            <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
              <input
                type="text"
                placeholder="Script Name (single word)"
                value={scriptName}
                onChange={(e) => handleScriptNameChange(e.target.value)}
                className="flex-1 p-2 bg-gray-800 border border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Script Name"
              />
              <select
                value={language}
                onChange={handleLanguageChange}
                className="p-2 bg-gray-800 border border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Select Programming Language"
              >
                <option value="python">Python</option>
                <option value="javascript">JavaScript</option>
              </select>
            </div>
            <textarea
              placeholder="Script Purpose"
              value={scriptPurpose}
              onChange={(e) => setScriptPurpose(e.target.value)}
              className="w-full p-2 bg-gray-800 border border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
              aria-label="Script Purpose"
            />
            <div className="relative flex-1 min-h-0">
              <AceEditor
                mode={language}
                theme="dracula"
                value={code}
                onChange={handleCodeChange}
                name="code-editor"
                editorProps={{ $blockScrolling: true }}
                setOptions={{
                  enableBasicAutocompletion: true,
                  enableLiveAutocompletion: true,
                  showLineNumbers: true,
                  tabSize: 2,
                  wrap: true,
                  fontSize: 14,
                  useWorker: false,
                }}
                onLoad={(editor) => {
                  editor.commands.on('afterExec', (e) => {
                    if (e.command.name === 'insertstring' && (e.args === '.' || e.args === '(')) {
                      console.log(`${e.args} typed, triggering autocomplete`);
                      editor.execCommand('startAutocomplete');
                    }
                  });
                }}
                width="100%"
                height="100%"
                className="border border-gray-700 rounded"
                aria-label="Code Editor"
              />
            </div>
            {error && <div className="text-red-500" role="alert">{error}</div>}
            {simulationStatus && (
              <div
                className={simulationStatus.success ? 'text-green-500' : 'text-red-500'}
                role="alert"
              >
                {renderWithNewlines(simulationStatus.message)}
              </div>
            )}
            <div className="flex space-x-4">
              <button
                onClick={simulateScript}
                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                aria-label="Simulate Script"
              >
                Simulate Script
              </button>
              <button
                onClick={saveScript}
                className={`px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  simulationStatus?.success && !error && simulationStatus?.result
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-gray-600 cursor-not-allowed'
                }`}
                disabled={!simulationStatus?.success || error || !simulationStatus?.result}
                aria-label="Save Script"
              >
                Save Script
              </button>
              <button
                onClick={formatCode}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                aria-label="Format Code"
              >
                Format Code
              </button>
            </div>
          </div>
        )}
        {activeTab === 'scripts' && (
          <div className="space-y-4 flex-1 overflow-auto" role="tabpanel">
            <h2 className="text-xl inline-flex items-center">
              Saved Scripts
              <button
                onClick={fetchScripts}
                className="ml-2 px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Refresh Scripts"
              >
                Refresh
              </button>
            </h2>
            {scripts.length === 0 && <p>No scripts saved yet.</p>}
            {scripts.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-700">
                  <thead>
                    <tr className="bg-gray-800">
                      <th className="border border-gray-700 p-2 text-left">Script ID</th>
                      <th className="border border-gray-700 p-2 text-left">Name</th>
                      <th className="border border-gray-700 p-2 text-left">Status</th>
                      <th className="border border-gray-700 p-2 text-left">Result</th>
                      <th className="border border-gray-700 p-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scripts.map((script) => (
                      <React.Fragment key={script.id}>
                        <tr className="bg-gray-900">
                          <td className="border border-gray-700 p-2">{script.id}</td>
                          <td className="border border-gray-700 p-2">{script.name}</td>
                          <td className="border border-gray-700 p-2">{script.status}</td>
                          <td className="border border-gray-700 p-2">{renderWithNewlines(truncateResult(script.result))}</td>
                          <td className="border border-gray-700 p-2">
                            <button
                              onClick={() => handleEdit(script)}
                              className="px-2 py-1 bg-transparent hover:bg-yellow-700 rounded mr-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                              aria-label={`Edit script ${script.name}`}
                            >
                              <FaEdit className="text-yellow-600" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(script.id, script.name)}
                              className="px-2 py-1 bg-transparent hover:bg-red-700 rounded mr-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                              aria-label={`Delete script ${script.name}`}
                            >
                              <FaTrash className="text-red-600" />
                            </button>
                            <button
                              onClick={() => toggleScriptExpand(script.id)}
                              className="px-2 py-1 bg-transparent hover:bg-gray-500 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
                              aria-label={`${script.expanded ? 'Collapse' : 'Expand'} script ${script.name}`}
                            >
                              {script.expanded ? <FaCompress className="text-blue-600" /> : <FaExpand className="text-blue-600" />}
                            </button>
                          </td>
                        </tr>
                        {script.expanded && (
                          <tr>
                            <td colSpan="5" className="border border-gray-700 p-4 bg-gray-800">
                              <div>
                                <p><span className="font-bold">Purpose:</span> {script.purpose || 'N/A'}</p>
                                <p><span className="font-bold">Language:</span> {script.language || 'N/A'}</p>
                                <p><span className="font-bold">Processes:</span></p>
                                <ul className="list-disc pl-5">
                                  {script.processes.map((process, index) => (
                                    <li key={index}>{process}</li>
                                  ))}
                                </ul>
                                <pre className="bg-gray-800 p-2 rounded mt-2 overflow-auto">{script.code || 'N/A'}</pre>
                                {script.result && (
                                  <pre className="bg-gray-800 p-2 rounded mt-2 overflow-auto">
                                    <span className="font-bold">Result:</span> {script.result}
                                  </pre>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {showDeleteDialog && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-labelledby="delete-dialog-title">
                <div className="bg-gray-800 p-6 rounded-lg shadow-lg max-w-md w-full">
                  <h3 id="delete-dialog-title" className="text-lg font-bold mb-4">Confirm Deletion</h3>
                  <p className="mb-6">Are you sure you want to delete the script "{scriptToDelete?.name}"? This action cannot be undone.</p>
                  <div className="flex justify-end space-x-4">
                    <button
                      onClick={cancelDelete}
                      className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
                      aria-label="Cancel deletion"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDelete}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                      aria-label="Confirm deletion"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeEditor;